/**
 * Product service for Shopify operations
 * Handles all product-related API calls
 */

import { BaseShopifyService } from "../services/base";
import { GET_PRODUCT, GET_PRODUCTS } from "./queries";
import { mapGetProductsToResponse, mapGetProductToResponse } from "./mappers";
import {
  IProductQueryReqest,
  ISingleProductRequest,
  IOptionsRequest,
  IResponse,
} from "../../../models";
import { formatResponse } from "../../../core/utils";

export class ProductService extends BaseShopifyService {
  /**
   * Get products based on query parameters
   */
  async getProducts(
    params: IProductQueryReqest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Defaults
      const DEFAULT_FIRST = 20;
      const MAX_FIRST = 100;

      const first =
        typeof params.first === "number" && params.first > 0
          ? Math.min(params.first, MAX_FIRST)
          : DEFAULT_FIRST;

      const query = options?.query || GET_PRODUCTS;
      const variables = options?.variables || {
        ...params,
        first,
      };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_PRODUCTS", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch products due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result.data || !result.data.products) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No products found.",
          data: [],
          meta: { pagination: undefined },
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Products retrieved successfully.",
        data: mapGetProductsToResponse(result.data.products),
        meta: {
          pagination: result.data.products.pageInfo,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getProducts", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a product by ID
   */
  async getProduct(
    params: ISingleProductRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      const { id, handle } = params || {};

      if (!id && !handle) {
        return formatResponse({
          success: false,
          message: "No product ID or handle provided.",
        });
      }

      const query = options?.query || GET_PRODUCT;
      const variables: Record<string, any> = { ...(options?.variables || {}) };

      if (id) {
        const idStr = id.toString();
        variables.id = idStr.includes("gid://")
          ? idStr
          : `gid://shopify/Product/${idStr}`;
      } else if (handle) {
        variables.handle = handle.toString();
      }

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_PRODUCT", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch product due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result.data || !result.data.product) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No product found.",
          data: null,
          meta: undefined,
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Product retrieved successfully.",
        data: mapGetProductToResponse(result.data.product),
        meta: undefined,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getProduct", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get multiple products by their handles using batch query
   */
  async getProductsByHandles(
    handles: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      if (!handles || handles.length === 0) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No handles provided.",
          data: [],
          meta: undefined,
        });
      }

      // Build query string to filter by handles
      const handleQuery = handles
        .map((handle) => `handle:${handle}`)
        .join(" OR ");

      const query = options?.query || GET_PRODUCTS;

      const variables = options?.variables || {
        query: handleQuery,
        first: Math.min(handles.length, 100), // Limit to avoid too large queries
      };

      // ✅ Execute GraphQL query using existing GET_PRODUCTS
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in getProductsByHandles", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message:
            "Failed to fetch products by handles due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result.data || !result.data.products) {
        return formatResponse({
          success: true,
          message:
            "Request completed successfully. No products found for the provided handles.",
          data: [],
          meta: undefined,
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Products retrieved successfully by handles.",
        data: mapGetProductsToResponse(result.data.products),
        meta: {
          pagination: result.data.products.pageInfo,
          requestedHandles: handles,
          foundCount:
            result.data.products.nodes?.length ||
            result.data.products.edges?.length ||
            0,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getProductsByHandles", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
